// HomeworkPanel — post homework to a class (parents get a push), see what's
// been set, and do copy checking: mark each student's work done /
// incomplete / not submitted, optionally telling the parents of the latter.
import { useEffect, useState } from 'react';
import { Plus, Paperclip, Trash2, ClipboardCheck, Bell } from 'lucide-react';
import {
  getClasses, getHomework, createHomework, deleteHomework, getHomeworkAttachment,
  getHomeworkChecks, saveHomeworkChecks,
} from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, Modal, errText, inputCls, theadRowCls, EmptyRow, ClassSelect } from '../ui/Form';
import { fmtDate, todayISO, fileToUpload, openDataUrl } from '../ui/format';

const EMPTY = { class_id: '', subject: '', title: '', description: '', due_date: '', notify: true };
const STATUS = [
  { key: 'done', label: 'Done', cls: 'bg-success-tint text-success' },
  { key: 'incomplete', label: 'Incomplete', cls: 'bg-caution-tint text-caution' },
  { key: 'not_submitted', label: 'Not submitted', cls: 'bg-critical-tint text-critical' },
];

export default function HomeworkPanel() {
  const [classes, setClasses] = useState([]);
  const [filter,  setFilter]  = useState('');
  const [rows,    setRows]    = useState([]);
  const [form,    setForm]    = useState(null);   // null = closed
  const [file,    setFile]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState('');
  const [checking, setChecking] = useState(null); // homework row

  useEffect(() => { getClasses().then(setClasses).catch(() => {}); }, []);
  useEffect(() => { load(); }, [filter]);

  async function load() {
    try { setRows(await getHomework(filter ? { class_id: filter } : {})); }
    catch (err) { setMsg(errText(err)); }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setMsg('');
    try {
      const payload = { ...form, class_id: Number(form.class_id), due_date: form.due_date || null };
      if (file) {
        payload.attachment = await fileToUpload(file);
        payload.attachment_name = file.name;
      }
      const r = await createHomework(payload);
      setMsg(form.notify
        ? `Posted to ${r.class_name} ✅ · pushed to ${r.notified_count ?? 0} parent phone${r.notified_count === 1 ? '' : 's'}`
        : `Posted to ${r.class_name} ✅`);
      setForm(null); setFile(null); load();
    } catch (err) { setMsg(errText(err)); }
    finally { setBusy(false); }
  }

  async function remove(hw) {
    if (!window.confirm(`Delete "${hw.title}" for ${hw.class_name}?`)) return;
    try { await deleteHomework(hw.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  async function openAttachment(hw) {
    try { openDataUrl((await getHomeworkAttachment(hw.id)).data_url); } catch (err) { setMsg(errText(err)); }
  }

  const today = todayISO();

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Homework</h2>
        <div className="flex gap-2">
          <ClassSelect classes={classes} value={filter} onChange={setFilter} allowAll />
          <Button onClick={() => setForm({ ...EMPTY, class_id: filter })} className="flex items-center gap-1.5"><Plus size={14} /> Give homework</Button>
        </div>
      </div>
      <Msg msg={msg} className="mb-3" />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={theadRowCls}>
                <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Class</th><th className="px-4 py-2.5">Subject</th>
                <th className="px-4 py-2.5">Homework</th><th className="px-4 py-2.5">Due</th>
                <th className="px-4 py-2.5">Copy checking</th><th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(hw => (
                <tr key={hw.id} className="border-t border-line-soft align-top">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(hw.assigned_date)}</td>
                  <td className="px-4 py-3 font-semibold">{hw.class_name}</td>
                  <td className="px-4 py-3">{hw.subject}</td>
                  <td className="px-4 py-3 max-w-[340px]">
                    <b className="text-ink">{hw.title}</b>
                    {hw.description && <span className="block text-ink-soft">{hw.description}</span>}
                    <span className="flex flex-wrap gap-3 mt-1 text-xs">
                      {hw.has_attachment && <button onClick={() => openAttachment(hw)} className="flex items-center gap-1 font-bold text-transit-deep hover:underline"><Paperclip size={12} /> {hw.attachment_name}</button>}
                      {hw.notified_count != null && <span className="flex items-center gap-1 text-ink-soft"><Bell size={12} /> {hw.notified_count} notified</span>}
                      {hw.created_by_name && <span className="text-ink-soft">by {hw.created_by_name}</span>}
                    </span>
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap ${hw.due_date && hw.due_date < today ? 'text-ink-soft' : 'font-semibold'}`}>{hw.due_date ? fmtDate(hw.due_date) : '—'}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setChecking(hw)} className="flex items-center gap-1.5 text-xs font-bold text-transit-deep hover:underline">
                      <ClipboardCheck size={14} /> {hw.checked}/{hw.class_size} checked
                    </button>
                    {hw.not_done > 0 && <span className="block text-xs font-bold text-critical mt-0.5">{hw.not_done} not done</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(hw)} aria-label="Delete homework" className="text-ink-soft hover:text-critical p-1"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <EmptyRow cols={7}>No homework yet — click Give homework</EmptyRow>}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <Modal title="Give homework" onClose={() => setForm(null)}>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Class"><ClassSelect classes={classes} value={form.class_id} onChange={v => setForm({ ...form, class_id: v })} required /></Field>
              <Field label="Subject"><input required className={inputCls} placeholder="e.g. Maths" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></Field>
            </div>
            <Field label="Homework"><input required maxLength={150} className={inputCls} placeholder="e.g. Exercise 4.1, questions 1–10" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Details (optional)"><textarea rows={3} className={inputCls} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Due date"><input type="date" min={today} className={inputCls} value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></Field>
              <Field label="Attachment (photo or PDF, optional)">
                <input type="file" accept="image/*,application/pdf" className="text-sm" onChange={e => setFile(e.target.files?.[0] || null)} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-ink">
              <input type="checkbox" checked={form.notify} onChange={e => setForm({ ...form, notify: e.target.checked })} className="accent-[var(--transit)]" />
              Send a push notification to parents of this class
            </label>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy}>{busy ? 'Posting…' : 'Post homework'}</Button>
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Cancel</Button>
            </div>
          </form>
        </Modal>
      )}

      {checking && <CopyCheckModal homework={checking} onClose={() => setChecking(null)} onSaved={(m) => { setMsg(m); setChecking(null); load(); }} />}
    </div>
  );
}

function CopyCheckModal({ homework, onClose, onSaved }) {
  const [rows,   setRows]   = useState(null);
  const [notify, setNotify] = useState(true);
  const [busy,   setBusy]   = useState(false);
  const [msg,    setMsg]    = useState('');

  useEffect(() => {
    getHomeworkChecks(homework.id).then(r => setRows(r.map(x => ({ ...x, status: x.status || 'done', remark: x.remark || '' }))))
      .catch(err => setMsg(errText(err)));
  }, [homework.id]);

  async function save() {
    setBusy(true); setMsg('');
    try {
      const r = await saveHomeworkChecks(homework.id, {
        checks: rows.map(x => ({ student_id: x.student_id, status: x.status, remark: x.remark || null })),
        notify_incomplete: notify,
      });
      onSaved(`Copy checking saved for ${r.saved} students ✅${r.notified ? ` · ${r.notified} parent${r.notified === 1 ? '' : 's'} notified` : ''}`);
    } catch (err) { setMsg(errText(err)); setBusy(false); }
  }

  const setAll = (status) => setRows(rows.map(x => ({ ...x, status })));

  return (
    <Modal title={`Copy checking — ${homework.class_name} · ${homework.subject}: ${homework.title}`} onClose={onClose} width={720}>
      <Msg msg={msg} className="mb-3" />
      {!rows ? <p className="text-sm text-ink-soft">Loading…</p> : (
        <>
          <div className="flex gap-2 mb-3 text-xs font-bold">
            <span className="text-ink-soft self-center">Mark all:</span>
            {STATUS.map(s => <button key={s.key} onClick={() => setAll(s.key)} className={`px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</button>)}
          </div>
          <div className="max-h-[50vh] overflow-y-auto border border-line rounded-s">
            {rows.map((r, i) => (
              <div key={r.student_id} className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-line-soft first:border-t-0">
                <span className="text-sm font-semibold text-ink flex-1 min-w-[140px]">{r.roll_number ? `${r.roll_number}. ` : ''}{r.name}</span>
                <div className="flex gap-1">
                  {STATUS.map(s => (
                    <button key={s.key} onClick={() => setRows(rows.map((x, j) => j === i ? { ...x, status: s.key } : x))}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border ${r.status === s.key ? `${s.cls} border-transparent` : 'border-line text-ink-soft'}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
                <input className={`${inputCls} !py-1 w-40`} placeholder="Remark" maxLength={200} value={r.remark}
                  onChange={e => setRows(rows.map((x, j) => j === i ? { ...x, remark: e.target.value } : x))} aria-label={`Remark for ${r.name}`} />
              </div>
            ))}
            {!rows.length && <p className="p-4 text-sm text-ink-soft">No students in this class.</p>}
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink mt-3">
            <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="accent-[var(--transit)]" />
            Notify parents of students marked incomplete / not submitted
          </label>
          <div className="flex gap-2 mt-3">
            <Button onClick={save} disabled={busy || !rows.length}>{busy ? 'Saving…' : 'Save'}</Button>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
          </div>
        </>
      )}
    </Modal>
  );
}
